import { Placeholder, Text } from '@lmn/design-system';
import styled from 'styled-components';
import type { JSX } from 'react';
import React from 'react';
import { useFetchHotelStatic } from '../hooks/useFetchHotelStatic';
import { useDestinationCityName } from '../hooks/useDestinationCityName';
import ShareButton from '../ShareButton';
import { useOffersContext } from '../OffersProvider';
import { useUrlSearchParams } from '../UrlSearchParamsProvider';
import { useTranslation } from '../../translations/Translation';

const Container = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;
const PackageReviewInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  padding-top: 8px;
`;
const TripOriginDestination = styled(Text).attrs({ variant: 'Title3' })``;

const DotSeparator = () => <span> · </span>;

export function PackageReviewSubheader(props: React.HTMLAttributes<HTMLDivElement>) {
  const { t } = useTranslation();
  const { data: hotelStaticData } = useFetchHotelStatic();
  const { offersData, isSoldout } = useOffersContext();
  const { totalRooms, adults, children, infants, isTrain } = useUrlSearchParams();

  // MPH-957: the destination is the one that was searched for, not the city of the airport the
  // return flight leaves from — those differ often enough (Vienna served by Bratislava) to name the
  // wrong trip. It resolves separately from the transport, so the title waits for both.
  const destinationCity = useDestinationCityName();
  const originTransportCity = offersData?.transport?.transports?.[0]?.outbound?.segments[0].departureHub.cityName;

  const {
    adults: adultsLabel,
    children: childrenLabel,
    rooms: roomsLabel,
  } = formatOccupancyExtended({ adults, children: children + infants, rooms: totalRooms }, t);

  return (
    <Container {...props}>
      <PackageReviewInfo>
        <Text variant="Caption" color="textDisabled" weight="bold">
          {t('vacation.select.package_review.step_title').toUpperCase()}
        </Text>
        {isSoldout ? undefined : (
          <TripOriginDestination>
            {originTransportCity && destinationCity ? (
              `${originTransportCity} – ${destinationCity}`
            ) : (
              <div
                css={`
                  padding: 4px 0;
                `}
              >
                <Placeholder width="220px" height="22px" />
              </div>
            )}
          </TripOriginDestination>
        )}
        <Text variant="BodySmall">
          <Occupancy
            labels={[
              `${isTrain ? t('vacation.common.trainplushotel') : t('vacation.common.flightplushotel')}`,
              adultsLabel,
              childrenLabel,
              roomsLabel,
            ].filter((label) => label)}
            Separator={DotSeparator}
          />
        </Text>
      </PackageReviewInfo>
      <ShareButton hotelName={hotelStaticData?.name || ''} className="info-share" />
    </Container>
  );
}

const occupancyKeys = {
  adult: 'vacation.common.adult',
  adults: 'vacation.common.adults',
  child: 'vacation.common.child',
  children: 'vacation.common.children',
  infant: 'vacation.common.infant',
  infants: 'vacation.common.infants',
  room: 'vacation.common.room',
  rooms: 'vacation.common.rooms',
};

export function formatOccupancyExtended(
  { adults, children, infants, rooms }: { adults: number; children?: number; infants?: number; rooms: number },
  t: (key: string, placeHolders?: { [key: string]: string }) => string
): { adults: string; children: string; infants: string; rooms: string } {
  const adultsLabel = `${adults} ${t(adults === 1 ? occupancyKeys.adult : occupancyKeys.adults)}`;
  const childrenLabel = children
    ? `${children} ${t(children === 1 ? occupancyKeys.child : occupancyKeys.children)}`
    : '';
  const infantsLabel = infants ? `${infants} ${t(infants === 1 ? occupancyKeys.infant : occupancyKeys.infants)}` : '';
  const roomsLabel = `${rooms} ${t(rooms === 1 ? occupancyKeys.room : occupancyKeys.rooms)}`;
  return { adults: adultsLabel, children: childrenLabel, infants: infantsLabel, rooms: roomsLabel };
}

export function formatOccupancy(
  { adults, children, infants }: { adults: number; children?: number; infants?: number },
  t: (key: string, placeHolders?: { [key: string]: string }) => string
): { adults: string; children: string; infants: string } {
  const adultsLabel = `${adults} ${t(adults === 1 ? occupancyKeys.adult : occupancyKeys.adults)}`;
  const childrenLabel = children
    ? `${children} ${t(children === 1 ? occupancyKeys.child : occupancyKeys.children)}`
    : '';
  const infantsLabel = infants ? `${infants} ${t(infants === 1 ? occupancyKeys.infant : occupancyKeys.infants)}` : '';
  return { adults: adultsLabel, children: childrenLabel, infants: infantsLabel };
}

export function Occupancy({ labels, Separator }: { labels: string[]; Separator: () => JSX.Element }) {
  return labels.map((label, index) => (
    <React.Fragment key={label}>
      <span
        css={`
          white-space: nowrap;
        `}
      >
        {label}
      </span>
      {index < labels.length - 1 && <Separator />}
    </React.Fragment>
  ));
}
